import { useT } from '../../i18n';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { parseJSON } from '../../components/ProjectForm';
import { InfoTip } from '../../components/InfoTip';

// [Proposition: collaborateurs-tache] A task has one assignee plus
// collaborators, both picked among the people involved in the project: its
// sponsor and the collaborators added on the project's Overview tab. Someone
// missing from the list isn't on the project yet — the reminder under the
// field says where to add them.

/** People involved in a project (sponsor first, then the Overview collaborators). */
export function projectPeople(project: { ownership?: unknown; resources_effort?: unknown } | null | undefined): string[] {
  if (!project) return [];
  const out: string[] = [];
  const add = (raw: unknown) => {
    const name = String(raw ?? '').trim();
    if (name && !out.some(n => n.toLowerCase() === name.toLowerCase())) out.push(name);
  };
  add(parseJSON<any>(project.ownership, {}).sponsor);
  for (const c of parseJSON<any>(project.resources_effort, {}).collaborators ?? []) add(c?.name);
  return out;
}

/** Reminder: the people lists only hold those added to the project upstream,
 *  on its Overview tab. An ⓘ next to the field label; its tooltip holds the
 *  explanation and the link to the project. */
export function ProjectPeopleHint({ projectId, align }: { projectId?: number | null; align?: 'start' | 'end' }) {
  const { t } = useT();
  return (
    <InfoTip label={t('collab.hint')} align={align}>
      {t('collab.hint')}
      {projectId ? <> <a href={`#project?id=${projectId}`}>{t('collab.hintLink')}</a></> : null}
    </InfoTip>
  );
}

export function TaskCollaboratorsField({ people, assignee, value, onChange, readOnly = false }: {
  people: string[];
  assignee: string | null | undefined;
  value: string[];
  onChange: (names: string[]) => void;
  readOnly?: boolean;
}) {
  const { t } = useT();
  const assigneeKey = String(assignee || '').trim().toLowerCase();
  // Names already on the task stay listed even if they've left the project.
  const names = [...people, ...value.filter(v => !people.some(p => p.toLowerCase() === v.toLowerCase()))]
    .filter(n => n.toLowerCase() !== assigneeKey);
  const selected = value.filter(v => v.toLowerCase() !== assigneeKey);

  if (readOnly) {
    return <div className="text-sm">{selected.length ? selected.join(', ') : <span className="text-ink-3">{t('collab.none')}</span>}</div>;
  }
  return (
    <div>
      <MultiSelectFilter
        label=""
        options={names.map(n => ({ value: n, label: people.includes(n) ? n : `${n} (${t('collab.notOnProject')})` }))}
        selected={selected}
        onChange={(v) => onChange(v as string[])}
        placeholder={t('collab.placeholder')}
      />
    </div>
  );
}
